import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Stack,
  Button,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
  ZoomIn as ZoomInIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import Header from "../Components/Utils/Header";
import Hero from "../Components/Landing/Hero";
import Footer from "../Components/Utils/Footer";
import EnquiryDialog from "../Components/Utils/EnquiryDialog";
import FAQ from "../Components/Utils/FAQ";
import ImageViewer from "../Components/Utils/ImageViewer";
import { motion, AnimatePresence } from "framer-motion";

// Import all art images
const ART_IMAGES = {
  ART001: require("../assets/images/art/art1.jpg"),
  ART002: require("../assets/images/art/art2.jpg"),
  ART003: require("../assets/images/art/art3.jpg"),
  ART004: require("../assets/images/art/art4.jpg"),
  ART005: require("../assets/images/art/art5.jpg"),
  ART006: require("../assets/images/art/art6.jpg"),
  ART007: require("../assets/images/art/art7.jpg"),
  ART008: require("../assets/images/art/art8.jpg"),
  ART009: require("../assets/images/art/art9.jpg"),
  ART010: require("../assets/images/art/art10.jpg"),
  ART011: require("../assets/images/art/art11.jpg"),
  ART012: require("../assets/images/art/art12.jpg"),
  ART013: require("../assets/images/art/art13.jpg"),
  ART014: require("../assets/images/art/art14.jpg"),
  ART015: require("../assets/images/art/art15.jpg"),
  ART016: require("../assets/images/art/art16.jpg"),
  ART017: require("../assets/images/art/art17.jpg"),
  ART018: require("../assets/images/art/art18.jpg"),
  ART019: require("../assets/images/art/art19.jpg"),
  ART020: require("../assets/images/art/art20.jpg"),
  ART021: require("../assets/images/art/art21.jpg"),
  ART022: require("../assets/images/art/art22.jpg"),
  ART023: require("../assets/images/art/art23.jpg"),
  ART024: require("../assets/images/art/art24.jpg"),
};

// Add art details
const ART_DETAILS = {
  ART001: {
    title: "ART001",
    category: "Original Art",
  },
  ART002: {
    title: "ART002",
    category: "Original Art",
  },
  ART003: {
    title: "ART003",
    category: "Original Art",
  },
  ART004: {
    title: "ART004",
    category: "Original Art",
  },
  ART005: {
    title: "ART005",
    category: "Original Art",
  },
  ART006: {
    title: "ART006",
    category: "Original Art",
  },
  ART007: {
    title: "ART007",
    category: "Original Art",
  },
  ART008: {
    title: "ART008",
    category: "Original Art",
  },
  ART009: {
    title: "ART009",
    category: "Original Art",
  },
  ART010: {
    title: "ART010",
    category: "Original Art",
  },
  ART011: {
    title: "ART011",
    category: "Original Art",
  },
  ART012: {
    title: "ART012",
    category: "Original Art",
  },
  ART013: {
    title: "ART013",
    category: "Original Art",
  },
  ART014: {
    title: "ART014",
    category: "Original Art",
  },
  ART015: {
    title: "ART015",
    category: "Original Art",
  },
  ART016: {
    title: "ART016",
    category: "Original Art",
  },
  ART017: {
    title: "ART017",
    category: "Original Art",
  },
  ART018: {
    title: "ART018",
    category: "Original Art",
  },
  ART019: {
    title: "ART019",
    category: "Original Art",
  },
  ART020: {
    title: "ART020",
    category: "Original Art",
  },
  ART021: {
    title: "ART021",
    category: "Original Art",
  },
  ART022: {
    title: "ART022",
    category: "Original Art",
  },
  ART023: {
    title: "ART023",
    category: "Original Art",
  },
  ART024: {
    title: "ART024",
    category: "Original Art",
  },
};

const ART_FAQS = [
  {
    question: "How can I purchase artwork?",
    answer:
      "You can enquire about any artwork by clicking the 'Enquire' button. We'll get back to you with pricing, availability, and shipping details.",
  },
  {
    question: "Do you ship internationally?",
    answer:
      "Yes, we offer worldwide shipping. Shipping costs vary depending on the artwork size and destination.",
  },
  {
    question: "Are the artworks original?",
    answer:
      "Yes, all our artworks are original pieces created by talented artists. We also offer limited edition prints of select pieces.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept various payment methods including M-Pesa, bank transfer, credit cards, and PayPal.",
  },
  {
    question: "Can I commission custom artwork?",
    answer:
      "Yes, we work with our artists to create custom pieces. Contact us with your requirements for more information.",
  },
];

const ITEMS_PER_PAGE = 9;

export default function Arts() {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedArt, setSelectedArt] = useState(null);
  const [openViewer, setOpenViewer] = useState(false);
  const [page, setPage] = useState(1);

  const handleOpenDialog = (art) => {
    setSelectedArt(art);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedArt(null);
  };

  const handleOpenViewer = (art) => {
    setSelectedArt(art);
    setOpenViewer(true);
  };

  const handleCloseViewer = () => {
    setOpenViewer(false);
  };

  const artworks = Object.entries(ART_IMAGES).map(([key, image]) => ({
    id: key,
    image,
    ...ART_DETAILS[key],
  }));

  // Calculate pagination
  const totalPages = Math.ceil(artworks.length / ITEMS_PER_PAGE);
  const paginatedArtworks = artworks.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // Add animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: "easeIn",
      },
    },
  };

  const itemVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.2 },
    },
  };

  const handlePageChange = (event, value) => {
    // Scroll to top with smooth animation
    window.scrollTo({
      top: document.querySelector(".gallery-section").offsetTop - 100,
      behavior: "smooth",
    });
    setPage(value);
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Header active="Art" />
      <Hero index={3} />

      <Box
        className="gallery-section"
        sx={{ py: { xs: 8, md: 12 }, bgcolor: "background.default" }}
      >
        <Container>
          <Stack spacing={6}>
            <Stack spacing={2} sx={{ textAlign: "center" }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Art Gallery
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Discover unique artworks from talented artists. Each piece tells
                a story and brings beauty to your space.
              </Typography>
            </Stack>

            <AnimatePresence mode="wait">
              <motion.div
                key={page}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <Grid container spacing={3}>
                  {paginatedArtworks.map((art, index) => (
                    <Grid item xs={12} sm={6} md={4} key={art.id}>
                      <motion.div variants={itemVariants}>
                        <Card
                          elevation={0}
                          sx={{
                            height: "100%",
                            borderRadius: 2,
                            overflow: "hidden",
                            transition: "all 0.3s ease-in-out",
                            border: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.1
                            )}`,
                            "&:hover": {
                              transform: "translateY(-8px)",
                              boxShadow: theme.shadows[8],
                              "& .overlay": {
                                opacity: 1,
                              },
                            },
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            <CardMedia
                              component="img"
                              height={320}
                              image={art.image}
                              alt={art.title}
                              sx={{
                                objectFit: "cover",
                              }}
                            />
                            <Box
                              className="overlay"
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: alpha(theme.palette.common.black, 0.7),
                                opacity: 0,
                                transition: "opacity 0.3s ease-in-out",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <Tooltip title="View Fullscreen">
                                <IconButton
                                  onClick={() => handleOpenViewer(art)}
                                  sx={{ color: "common.white" }}
                                >
                                  <ZoomInIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Enquire">
                                <IconButton
                                  onClick={() => handleOpenDialog(art)}
                                  sx={{ color: "common.white" }}
                                >
                                  <CartIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <CardContent>
                            <Stack spacing={1}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {art.title}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ color: "text.secondary" }}
                              >
                                <Typography variant="body2">
                                  {art.category}
                                </Typography>
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: "50%",
                                    bgcolor: "text.disabled",
                                  }}
                                />
                                <Typography variant="body2">
                                  {art.medium}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {art.dimensions}
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleOpenDialog(art)}
                                startIcon={<InfoIcon />}
                                sx={{ mt: 1 }}
                              >
                                Enquire About This Piece
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </AnimatePresence>

            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Stack alignItems="center" sx={{ mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                          bgcolor: "primary.light",
                        },
                        "&.Mui-selected": {
                          transform: "scale(1.1)",
                          fontWeight: "bold",
                        },
                      },
                    }}
                  />
                </Stack>
              </motion.div>
            )}
          </Stack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <FAQ
        title="Art Purchase FAQs"
        subtitle="Common questions about buying artwork"
        faqs={ART_FAQS}
      />

      <Footer />

      <EnquiryDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedArt?.title}
      />

      <ImageViewer
        open={openViewer}
        onClose={handleCloseViewer}
        image={selectedArt?.image}
      />
    </Box>
  );
}
